import { AlertIconOwner } from "@images/svgIcons/illustrations/alertIcon"
import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffer } from "@modules/boxOffer/hooks/useBoxOffer"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { Archive } from "lucide-react"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type BoxOfferArchivedModalRef = {
  open: () => void
}

export type BoxOfferArchivedModalProps = {
  offer: BoxOfferDetailedEntity
}

export function BoxOfferArchivedModal({
  offer,
  ref,
}: {
  offer: BoxOfferDetailedEntity
  ref: React.Ref<BoxOfferArchivedModalRef>
}) {
  const [showModal, setShowModal] = useState(false)
  const theme = useTheme()
  const { deleteBoxOffer } = useBoxOffer(offer.id)
  const toast = useToast()
  const { hideSidebar } = useSidebar()

  const handleArchiveOffer = () => {
    deleteBoxOffer()
      .then(() => {
        toast.show({
          severity: "success",
          summary: word("boxOffer.archivedModal.toast.success"),
        })
        setShowModal(false)
        hideSidebar()
      })
      .catch(() => {
        toast.show({
          severity: "success",
          summary: word("boxOffer.archivedModal.toast.error"),
        })
      })
  }

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  let dot = {
    one: "",
    two: "",
  }

  if (offer.type === BoxOfferType.Subscription) {
    dot = {
      one: word("boxOffer.archivedModal.dot.three"),
      two: word("boxOffer.archivedModal.dot.four"),
    }
  } else if (offer.type === BoxOfferType.Millenium) {
    dot = {
      one: word("boxOffer.archivedModal.dot.three"),
      two: word("boxOffer.archivedModal.dot.two"),
    }
  } else {
    dot = {
      one: word("boxOffer.archivedModal.dot.one"),
      two: word("boxOffer.archivedModal.dot.two"),
    }
  }

  return (
    <HUDialog visible={showModal} header={""} onHide={onHide} style={{ width: "418px" }}>
      {/* HEADER */}
      <div className="flex flex-column gap-3 align-items-center ">
        <AlertIconOwner color="" />
        <HUText fontStyle="TL" color={theme.mainWhite} className="text-center">
          {word(`boxOffer.archivedModal.title.${offer.type}`)}
        </HUText>
        <ul className="flex flex-column gap-2 pl-4">
          {Object.values(dot).map((item, index) => (
            <li key={index}>
              <HUText fontStyle="BM" color={theme.mainWhite}>
                {item}
              </HUText>
            </li>
          ))}
          <li>
            <HUText fontStyle="BM" color={theme.mainWhite}>
              {word(`boxOffer.archivedModal.dot.${offer.type}`)}
            </HUText>
          </li>
        </ul>
      </div>
      {/* BUTTON */}
      <div className=" mb-4 mt-4 flex flex-row gap-3 align-items-center justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={word("global.cancel")}
          onClick={() => onHide()}
          className="w-full"
        />
        <HUButton
          text={word("global.archive")}
          onClick={() => handleArchiveOffer()}
          type={"Right_Icon"}
          size="M"
          colorType="Primary"
          fullWidth
          icon={{
            iconView: <Archive strokeWidth={1.5} size={15} color={theme.mainWhite} />,
          }}
        />
      </div>
    </HUDialog>
  )
}
